import React, { useEffect, useState } from 'react';
import { Button, Image, Stack } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import parse from 'html-react-parser';
import { sanitizeHtml } from 'components/sanitizeHtml';
import { logJobView, useJobDetailsQuery } from 'services';
import { useNavigate, useParams } from 'react-router-dom';
import { S3_URL, USER_TYPES } from 'constants/index';
import { getCreatedDifferenceInBestUnit, unitNumber } from 'utils';
import useTranslate from 'hooks/useTranslate';
import CompanyDetailsBox from 'components/Common/Company/CompanyDetailsBox';
import { setCompanyId } from 'store/company';
import JobApplyBtn from 'components/Common/ActionButton/JobApplyBtn';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import JobDetailSkeleton from 'components/Common/Skeleton/JobDetailSkeleton';
import useSkeleton from 'hooks/useSkeleton';
import './styles.scss';
import useLocalizationStore from 'store/localization';

const JobDetailBox: React.FC = () => {
	const navigate = useNavigate();
	const { activeLanguage }: any = useLocalizationStore((state) => state);
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	const [isJobApply, setIsJobApply] = useState<boolean>(false);
	const [jobApplyStatus, setJobApplyStatus] = useState<boolean>(false);
	const { translate } = useTranslate();
	const { id } = useParams<any>();
	const { data: job = {}, refetch } = useJobDetailsQuery(id);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);

	const authUser: any = useAuthStore((state) => state.userInfo.user);

	useEffect(() => {
		if (id) {
			logJobView(id);
		}
	}, []);

	useEffect(() => {
		setIsLoading(true);
		refetch().finally(() => {
			setIsLoading(false);
		});
	}, [id, refetch, activeLanguage]);

	useEffect(() => {
		setJobApplyStatus(job?.jobApplyStatus || 'Applied');
		setIsJobApply(job?.isJobApply || false);
	}, [job]);

	useEffect(() => {
		if (job?.companyId) setCompanyId(job?.companyId);
		return () => setCompanyId(null);
	}, [job?.companyId]);

	const handleClick = () => {
		if (!authUser?.userId) {
			setModalConfig({
				visible: true,
				type: 'loginModal',
			});
			return false;
		}
		return true;
	};

	return (
		<>
			{delayedLoading ? (
				<JobDetailSkeleton />
			) : (
				<Stack className="job-detail-box bg-white">
					<div className="job-info d-flex align-items-center gap-3 position-relative">
						<div className="d-flex flex-column flex-md-row align-items-md-center flex-1 gap-3">
							<div className="company-logo">
								<Image
									src={
										job?.companyLogo
											? `${S3_URL}${job.companyLogo}`
											: IMAGE_PATHS.CompanyLogoDefault
									}
									alt="logo"
									className="w-100 h-100 object-fit-cover"
									loading="lazy"
								/>
							</div>
							<Stack className="job-detail justify-content-center flex-1">
								<h4 className="mb-0 fw-bold">{job?.title}</h4>
								<p className="company-name mb-0">
									{job?.isJoobleJob ? job?.joobleJobCompany : job?.companyName}
								</p>
							</Stack>
						</div>
						<div className="apply-btn">
							{(!authUser?.userType || authUser?.userType !== USER_TYPES?.COMPANY) && (
								<JobApplyBtn
									isApplied={isJobApply}
									setIsApplied={setIsJobApply}
									jobId={Number(job?.jobId)}
									isJobAssessment={job?.isJobAssessment || false}
									handleClick={handleClick}
									modalConfig={modalConfig}
									setModalConfig={setModalConfig}
									jobApplyStatus={jobApplyStatus}
								/>
							)}
						</div>
					</div>
					<div className="employee-info">
						<h5 className="fw-bold mb-3">{translate('JobDetailBox.employmentInformation')}</h5>
						<div className="employee-info-content d-flex flex-wrap">
							{job?.jobType && (
								<div className="employee-info-box d-flex align-items-center gap-2">
									<div className="info-icon d-flex align-items-center justify-content-center">
										<i className="ri-time-line"></i>
									</div>
									<div>
										<p className="mb-1">{translate('JobDetailBox.jobType')}</p>
										<h6 className="mb-0 fw-bold">{job.jobType}</h6>
									</div>
								</div>
							)}
							<div className="employee-info-box d-flex align-items-center gap-2">
								<div className="info-icon d-flex align-items-center justify-content-center">
									<i className="ri-wallet-3-line"></i>
								</div>
								<div>
									<p className="mb-1">{translate('JobDetailBox.salary')}</p>
									<h6 className="mb-0 fw-bold">
										{job?.currency}
										{[unitNumber(job?.minAmount, 1), unitNumber(job?.maxAmount, 1)]
											.filter(Boolean)
											.join('-')}
										<span>{job?.salaryType && `(${job.salaryType})`}</span>
									</h6>
								</div>
							</div>
							<div className="employee-info-box d-flex align-items-center gap-2">
								<div className="info-icon d-flex align-items-center justify-content-center">
									<i className="ri-group-line"></i>
								</div>
								<div>
									<p className="mb-1">{translate('JobDetailBox.applicants')}</p>
									<h6 className="mb-0 fw-bold">{unitNumber(job?.totalJobApply, 0)}</h6>
								</div>
							</div>
							{job?.level?.title && (
								<div className="employee-info-box d-flex align-items-center gap-2">
									<div className="info-icon d-flex align-items-center justify-content-center">
										<i className="ri-award-line"></i>
									</div>
									<div>
										<p className="mb-1">{translate('JobDetailBox.experience')}</p>
										<h6 className="mb-0 fw-bold">{job.level?.title}</h6>
									</div>
								</div>
							)}
							<div className="employee-info-box d-flex align-items-center gap-2">
								<div className="info-icon d-flex align-items-center justify-content-center">
									<i className="ri-list-check-2"></i>
								</div>
								<div>
									<p className="mb-1">{translate('JobDetailBox.assessment')}</p>
									<h6 className="mb-0 fw-bold">
										{job?.isJobAssessment ? translate('Common.yes') : translate('Common.no')}
									</h6>
								</div>
							</div>

							{job?.createdAt && (
								<div className="employee-info-box d-flex align-items-center gap-2">
									<div className="info-icon d-flex align-items-center justify-content-center">
										<i className="ri-calendar-line"></i>
									</div>
									<div>
										<p className="mb-1">{translate('JobDetailBox.jobPosted')}</p>
										<h6 className="mb-0 fw-bold">
											{getCreatedDifferenceInBestUnit(job.createdAt)}
										</h6>
									</div>
								</div>
							)}
						</div>
						{authUser?.userId === job?.companyId && (
							<div className="posted-job-box-actions d-flex gap-2 gap-sm-3 justify-content-sm-end mt-4">
								{job.totalJobApply > 0 && (
									<>
										<Button
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/company/applicants/${job?.jobId}`);
											}}
											className="transparent-btn rounded-pill lh-1"
										>
											{translate('Company.applicants')}
										</Button>
										<Button
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/company/hiring-board/${job?.jobId}`);
											}}
											className="filled-btn rounded-pill lh-1"
										>
											{translate('Company.applicationBoard')}
										</Button>
									</>
								)}
							</div>
						)}
					</div>
					<div className="basic-info">
						<h5 className="fw-bold mb-3">{translate('JobDetailBox.basicInformation')}</h5>
						<Stack className="gap-2 gap-md-3">
							{job?.industryName && (
								<div className="d-flex align-items-center gap-2">
									<i className="ri-gallery-view-2 basic-info-icon lh-1"></i>
									<h6 className="mb-0">{job.industryName}</h6>
								</div>
							)}
							{job?.jobRoleData?.length > 0 && (
								<div className="d-flex align-items-center gap-2">
									<i className="ri-customer-service-line basic-info-icon lh-1"></i>
									<h6 className="mb-0">
										{job?.jobRoleData.map((role: any) => role.title).join(', ')}
									</h6>
								</div>
							)}
							{job?.level?.[0]?.title && (
								<div className="d-flex align-items-center gap-2">
									<i className="ri-bar-chart-2-line basic-info-icon lh-1"></i>
									<h6 className="mb-0">{job.level[0].title}</h6>
								</div>
							)}
							<div className="d-flex align-items-center gap-2">
								<i className="ri-map-pin-line basic-info-icon lh-1"></i>
								<h6 className="mb-0">
									{[job?.cityName, job?.stateName, job?.countryName].filter(Boolean).join(', ')}
								</h6>
							</div>
						</Stack>
					</div>

					{job?.skills && job?.skills.length > 0 && (
						<div className="job-description">
							<h5 className="fw-bold mb-3">{translate('JobDetailBox.roles')}</h5>
							<div className="key-skills-content d-flex flex-wrap">
								{job?.jobRoleData?.map((role: any, index: number) => (
									<div className="key-skills-box rounded-pill" key={index}>
										<h6 className="mb-0">{role?.title}</h6>
									</div>
								))}
							</div>
						</div>
					)}

					{job?.description && (
						<div className="job-description">
							<h5 className="fw-bold mb-3">{translate('JobDetailBox.jobDescription')}</h5>
							<div className="job-description-content">
								<p>{parse(sanitizeHtml(job.description.replace(/\n/g, '<br>')))}</p>
							</div>
						</div>
					)}
					{job?.skills?.length > 0 && (
						<div className="key-skills">
							<h5 className="fw-bold mb-3">{translate('JobDetailBox.keySkillAndTools')}</h5>
							<div className="key-skills-content d-flex flex-wrap my-3">
								{job.skills?.map((skill: any, index: number) => (
									<div key={index} className="key-skills-box rounded-pill">
										<h6 className="mb-0">{skill?.title}</h6>
									</div>
								))}
							</div>
						</div>
					)}
					<CompanyDetailsBox handleClick={handleClick} company={job} />
				</Stack>
			)}
		</>
	);
};

export default JobDetailBox;
